import * as React from 'react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Callout, Position } from '@blueprintjs/core'
import { DepopSpecificSelect } from './DepopSpecificSelect'
import { useDepopChannelFormContext } from '../../../../products_v4/listings/depop/DepopChannelFormContext'
import { Spinner } from '../../../../../components/Spinner'
import { useDepopSpecifics } from './useDepopSpecifics'
import { useDepopCategorySpecifics } from './useDepopCategorySpecifics'

// Depop's own catalogue marks every specific optional, but brand_name is in
// DEPOP_REQUIRED_LISTING_DATA_FIELDS on the marketplace-service side and a listing without it is
// rejected, so we surface it as required regardless of what the catalogue says.
const ALWAYS_REQUIRED = ['brand']

const GUTTER_TOOLTIP_BY_COLUMN = [
  { offset: 45, position: Position.LEFT },
  { offset: 380, position: Position.LEFT },
  { offset: 196, position: Position.RIGHT }
]

interface Props {
  sectionOpen: boolean
  cellsBefore: number
  brandError?: any
}

// Depop ships no name_i18n for `colour`, so Depop::SpecificsParser falls back to the raw id and the
// label arrives lowercase and British. Only the label is overridden — the key stays `colour`,
// which is what Depop's payload expects.
const LABEL_OVERRIDES: Record<string, string> = {
  colour: 'Color'
}

// Depop stores a specific's value as a string when it accepts one value and as a string[] when it
// accepts several (maxValues > 1). Normalize whatever is currently stored to the shape the matching
// select expects, coercing legacy values that were saved under the other shape.
const normalizeValue = (raw: string | string[] | undefined, multi: boolean): string | string[] => {
  if (multi) {
    if (Array.isArray(raw)) return raw
    return raw ? [raw] : []
  }
  if (Array.isArray(raw)) return raw[0] ?? null
  return raw ?? null
}

export const Specifics: FC<Props> = ({ sectionOpen, cellsBefore, brandError }) => {
  const { form, onListingChange } = useDepopChannelFormContext()
  const { specifics, valuesByName, loading, error } = useDepopSpecifics()
  const {
    specifics: categorySpecifics,
    valuesByName: categoryValuesByName,
    loading: categoryLoading,
    error: categoryError
  } = useDepopCategorySpecifics(form.categoryMarketCategoryId)

  const globalNames = useMemo(() => specifics.map(s => s.name), [specifics])

  const selected: Record<string, string | string[]> = useMemo(
    () => (form.specifics || []).reduce((acc, s) => ({ ...acc, [s.name]: s.value }), {}),
    [form.specifics]
  )

  const onSpecificChange = useCallback(
    (name: string, value: string | string[]) => {
      const next = { ...selected, [name]: value }
      const specificsList = Object.keys(next)
        .filter(key => {
          const v = next[key]
          if (v === null || v === undefined || v === '') return false
          return !(Array.isArray(v) && v.length === 0)
        })
        .map(key => ({ name: key, value: next[key] }))

      onListingChange({ name: 'specifics', value: specificsList })
    },
    [selected, onListingChange]
  )

  // Drop any specifics belonging to a previously selected category while keeping the always-present
  // global ones. We only reconcile once both specific sets have settled (and not on a category
  // error) so we never discard the current category's saved values mid-load or on a transient
  // fetch failure.
  useEffect(() => {
    if (loading || categoryLoading || categoryError) return
    const validNames = new Set([...globalNames, ...categorySpecifics.map(s => s.name)])
    const current = form.specifics || []
    const pruned = current.filter(s => validNames.has(s.name))
    if (pruned.length !== current.length) {
      onListingChange({ name: 'specifics', value: pruned })
    }
  }, [loading, categoryLoading, categoryError, globalNames, categorySpecifics, form.specifics, onListingChange])

  if (error) return <Callout intent="warning">{error}</Callout>
  if (loading) return <Spinner />

  return (
    <>
      {specifics.map((specific, index) => {
        const multi = (specific.validationRules?.maxValues ?? 1) > 1
        const isBrand = specific.name === 'brand'
        const gutterTooltip = GUTTER_TOOLTIP_BY_COLUMN[(cellsBefore + index) % 3]
        return (
          <DepopSpecificSelect
            key={specific.name}
            specificName={LABEL_OVERRIDES[specific.name] || specific.displayName || specific.name}
            value={normalizeValue(selected[specific.name], multi)}
            options={valuesByName[specific.name] || []}
            required={specific.required || ALWAYS_REQUIRED.includes(specific.name)}
            maxValues={specific.validationRules?.maxValues}
            error={isBrand ? brandError : undefined}
            errorTooltipOffset={sectionOpen && gutterTooltip.offset}
            errorTooltipPosition={gutterTooltip.position}
            onChange={v => onSpecificChange(specific.name, v)}
          />
        )
      })}
      {categoryError && <Callout intent="warning">{categoryError}</Callout>}
      {!categoryError && categoryLoading && <Spinner />}
      {!categoryError &&
        !categoryLoading &&
        categorySpecifics.map(specific => {
          const multi = (specific.validationRules?.maxValues ?? 1) > 1
          return (
            <DepopSpecificSelect
              key={specific.name}
              specificName={specific.displayName || specific.name}
              value={normalizeValue(selected[specific.name], multi)}
              options={categoryValuesByName[specific.name] || []}
              required={specific.required}
              maxValues={specific.validationRules?.maxValues}
              onChange={v => onSpecificChange(specific.name, v)}
            />
          )
        })}
    </>
  )
}
